import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AppTheme = 'classic' | 'ocean' | 'charcoal' | 'ocean-v2' | 'charcoal-v2';

export interface ThemeDef {
  id: AppTheme;
  /** klucz i18n nazwy skorki w selektorze */
  labelKey: string;
  /** nakladka motywu PrimeNG (plik w assets/themes); null = classic (luna-amber z bundla) */
  primeOverride: string | null;
  /**
   * Paleta tokenow (wartosc data-theme na <html>). Skorki "v2" NIE maja
   * wlasnej palety - dziedzicza baze (ocean/charcoal), dzieki czemu caly
   * istniejacy CSS skorek ([data-theme=...] w tokens.css / skins.css /
   * material-skins.scss / nakladki PrimeNG) dziala dla nich bez zmian.
   */
  base: 'classic' | 'ocean' | 'charcoal';
  /**
   * Uklad widokow dokumentow (zamowienie/faktura): 'v2' dostaje atrybut
   * data-layout="v2" na <html> + flage w komponentach formularzy (hero,
   * gestszy formularz - koncept B z Claude Design, 08.2026). 'v1' = obecny
   * uklad bez zmian.
   */
  layout: 'v1' | 'v2';
}

/**
 * NEX-249 — przelaczanie skorek panelu Office w runtime.
 * Mechanizm celowo warstwowy, bez dotykania logiki komponentow:
 *  - atrybut data-theme na <html> steruje tokenami CSS + motywem Material
 *    (zasieg [data-theme] w tokens.css / material-skins.scss / skins.css),
 *  - <link id="app-prime-theme"> podmienia caly motyw PrimeNG
 *    (jeden motyw na raz — brak konfliktu dwoch themeow).
 * Skorka klasyczna = wartosci domyslne, zero nadpisan.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'app_theme';
  private readonly defaultTheme: AppTheme = 'classic';
  private readonly linkId = 'app-prime-theme';

  readonly themes: ThemeDef[] = [
    { id: 'classic',     labelKey: 'MENU.THEME.CLASSIC',     primeOverride: null,                              base: 'classic',  layout: 'v1' },
    { id: 'ocean',       labelKey: 'MENU.THEME.OCEAN',       primeOverride: 'assets/themes/theme-ocean.css',    base: 'ocean',    layout: 'v1' },
    { id: 'charcoal',    labelKey: 'MENU.THEME.CHARCOAL',    primeOverride: 'assets/themes/theme-charcoal.css', base: 'charcoal', layout: 'v1' },
    { id: 'ocean-v2',    labelKey: 'MENU.THEME.OCEAN_V2',    primeOverride: 'assets/themes/theme-ocean.css',    base: 'ocean',    layout: 'v2' },
    { id: 'charcoal-v2', labelKey: 'MENU.THEME.CHARCOAL_V2', primeOverride: 'assets/themes/theme-charcoal.css', base: 'charcoal', layout: 'v2' },
  ];

  private current: AppTheme = this.defaultTheme;

  /** Emituje przy kazdej zmianie skorki - formularze dokumentow przelaczaja
   *  uklad (hero v2) na zywo, bez przeladowania strony. */
  private readonly themeSubject = new BehaviorSubject<AppTheme>(this.defaultTheme);
  readonly theme$ = this.themeSubject.asObservable();

  /** Wywolywane przy starcie aplikacji (APP_INITIALIZER). */
  init(): void {
    const saved = localStorage.getItem(this.storageKey) as AppTheme | null;
    const theme = this.isKnown(saved) ? (saved as AppTheme) : this.defaultTheme;
    this.setTheme(theme, false);
  }

  getCurrentTheme(): AppTheme {
    return this.current;
  }

  /** Czy biezaca skorka uzywa ukladu v2 widokow dokumentow (koncept B). */
  isV2Layout(): boolean {
    return this.def(this.current)?.layout === 'v2';
  }

  setTheme(theme: AppTheme, persist = true): void {
    if (!this.isKnown(theme)) {
      theme = this.defaultTheme;
    }
    this.current = theme;

    const def = this.def(theme)!;
    // data-theme = PALETA (baza), nie id skorki - patrz komentarz w ThemeDef.
    document.documentElement.setAttribute('data-theme', def.base);
    if (def.layout === 'v2') {
      document.documentElement.setAttribute('data-layout', 'v2');
    } else {
      document.documentElement.removeAttribute('data-layout');
    }
    this.applyPrimeTheme(theme);

    if (persist) {
      localStorage.setItem(this.storageKey, theme);
    }
    this.themeSubject.next(theme);
  }

  private def(theme: AppTheme): ThemeDef | undefined {
    return this.themes.find((t) => t.id === theme);
  }

  private applyPrimeTheme(theme: AppTheme): void {
    const def = this.themes.find((t) => t.id === theme);
    const existing = document.getElementById(this.linkId) as HTMLLinkElement | null;

    // classic (lub brak nakladki) => usuwamy link; zostaje bundlowany luna-amber
    if (!def || !def.primeOverride) {
      if (existing) {
        existing.parentNode?.removeChild(existing);
      }
      return;
    }

    // Ocean/Charcoal: nakladka MUSI byc na koncu <head> (po bundlu z luna-amber),
    // zeby ja nadpisac. Re-append gwarantuje poprawna kolejnosc kaskady.
    let link = existing;
    if (!link) {
      link = document.createElement('link');
      link.id = this.linkId;
      link.rel = 'stylesheet';
    }
    if (link.getAttribute('href') !== def.primeOverride) {
      link.setAttribute('href', def.primeOverride);
    }
    document.head.appendChild(link);
  }

  private isKnown(theme: string | null): boolean {
    return !!theme && this.themes.some((t) => t.id === theme);
  }
}
